import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import MenuBack from "../components/MenuBack";
import MissionArt from "../components/MissionArt";
import { MISSIONS, isMissionLocked } from "../content/missions";
import { shouldHandleMenuKey } from "../lib/menuKeys";

// Campaign mode: the mission select screen.
export default function CampaignList() {
  const [selected, setSelected] = useState(0);
  const refs = useRef([]);
  const listRef = useRef(null);

  const move = useCallback(
    (delta) => {
      const next = (selected + delta + MISSIONS.length) % MISSIONS.length;
      setSelected(next);
      refs.current[next]?.focus();
    },
    [selected]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (!shouldHandleMenuKey(e, listRef.current)) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        move(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        move(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  const current = MISSIONS[selected];
  const currentLocked = isMissionLocked(current);

  return (
    <div className="bg-[#06080C] text-white min-h-screen">
      <MenuBack trail="Mode 02 · Campaign" />

      <section className="border-b border-[#1F2330] relative overflow-hidden">
        <div className="absolute inset-0 warning-stripes opacity-20" />
        <div className="relative px-5 md:px-12 py-12 md:py-14">
          <div className="label mb-2" style={{ color: "#E8692E" }}>/ Campaign · Mission Files</div>
          <h1
            data-testid="campaigns-heading"
            className="font-display text-5xl sm:text-7xl uppercase tracking-[0.08em] etched leading-none"
          >
            Mission Select
          </h1>
          <p className="text-[#A0A6B5] mt-4 max-w-2xl">
            Every design has a story. Choose a mission to open the file and
            gear up.
          </p>
        </div>
      </section>

      <section className="px-5 md:px-12 py-10 md:py-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-start">
        {/* Mission list */}
        <ol ref={listRef} className="space-y-3" data-testid="mission-list">
          {MISSIONS.map((m, idx) => {
            const locked = isMissionLocked(m);
            const active = selected === idx;
            const inner = (
              <div className="relative flex items-center gap-4 md:gap-6 px-4 md:px-6 py-5">
                <span
                  className={`font-mono text-xs w-3 transition-opacity ${active ? "opacity-100" : "opacity-0"}`}
                  style={{ color: locked ? "#8A91A0" : m.accent }}
                  aria-hidden="true"
                >
                  ▶
                </span>
                <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#8A91A0] shrink-0">
                  Mission{" "}
                  <span style={{ color: locked ? "#8A91A0" : m.accent }}>{m.code}</span>
                </div>
                <div className="h-px w-4 md:w-8 bg-[#2A3040] shrink-0" />
                <div
                  className={`flex-1 font-display text-xl sm:text-2xl md:text-3xl uppercase tracking-[0.1em] leading-tight ${
                    locked ? "text-[#8A91A0]" : "text-white"
                  }`}
                >
                  {locked ? (
                    <>
                      Classified{" "}
                      <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] align-middle">
                        / Coming Soon
                      </span>
                    </>
                  ) : (
                    m.name
                  )}
                </div>
                {locked ? (
                  <Lock className="w-4 h-4 text-[#8A91A0] shrink-0" />
                ) : (
                  <ArrowRight
                    className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1"
                    style={{ color: m.accent }}
                  />
                )}
              </div>
            );

            const shared = {
              ref: (el) => (refs.current[idx] = el),
              onMouseEnter: () => setSelected(idx),
              onFocus: () => setSelected(idx),
              "data-testid": `mission-${m.code}`,
              style: { "--accent": locked ? "#2A3040" : m.accent },
              className: `group relative block border bg-[#11141C] transition-all outline-none focus-visible:ring-1 focus-visible:ring-white ${
                active ? "border-[var(--accent)]" : "border-[#1F2330]"
              } ${locked ? "cursor-not-allowed" : "hover:border-[var(--accent)]"}`,
            };

            return (
              <li key={m.slug}>
                {locked ? (
                  <div {...shared} tabIndex={0} role="link" aria-disabled="true" aria-label={`Mission ${m.code}: classified, coming soon`}>
                    <div className="absolute inset-0 warning-stripes opacity-20 pointer-events-none" />
                    {inner}
                  </div>
                ) : (
                  <Link {...shared} to={`/campaigns/${m.slug}`}>
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>

        {/* Preview panel */}
        <aside
          data-testid="mission-preview"
          className="border border-[#1F2330] bg-[#0A0D14] corners relative overflow-hidden lg:sticky lg:top-28"
          style={{ "--accent": current.accent }}
        >
          <div
            className="absolute inset-0 opacity-60"
            style={{
              background: `radial-gradient(circle at 50% 35%, ${currentLocked ? "#8C92A0" : current.accent}26 0%, transparent 65%)`,
            }}
          />
          <div className="relative p-6 md:p-8">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A91A0]">
              <span>Mission File // {current.code}</span>
              <span style={{ color: currentLocked ? "#8A91A0" : current.accent }}>
                {currentLocked ? "Locked" : "Available"}
              </span>
            </div>
            <MissionArt mission={current} className="my-6 aspect-square max-h-72 mx-auto" />
            <div
              className="font-display text-3xl md:text-4xl uppercase tracking-[0.1em] leading-none"
              style={{ color: currentLocked ? "#8A91A0" : current.accent }}
            >
              {currentLocked ? "Classified" : current.name}
            </div>
            <p className="mt-3 text-sm text-[#A0A6B5]">{current.tagline}</p>
            {!currentLocked && (
              <Link
                to={`/campaigns/${current.slug}`}
                className="mt-6 inline-flex items-center gap-2 border px-5 py-3 font-mono uppercase tracking-[0.3em] text-[11px] transition-colors hover:text-black hover:bg-[var(--accent)]"
                style={{ borderColor: current.accent, color: current.accent }}
              >
                Open Mission File <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
